import { useState, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconBolt, IconLock } from "@tabler/icons-react";
import emailjs from "@emailjs/browser";
import { trackGoogleAdsLeadFormConversion } from "../../../Services/analytics.service";
import { createEnquiry } from "../../../Services/enquiry.service";
import { buildWhatsAppUrl } from "../../../Services/contact.service";
import AlertModal from "../AlertModal";

interface GetHelpFormProps {
  formSize?: "sm" | "md" | "lg";
  flatForm?: boolean;
  headerMode?: "default" | "minimal";
  submitLabel?: string;
  showEmailField?: boolean;
}

interface LeadFormData {
  usersName: string;
  email: string;
  contactNumber: string;
  studyNeed: string;
  projectRequirements: string;
  allowMarketingNotifications: boolean;
}

type LeadFormErrors = Partial<Record<keyof LeadFormData, string>>;
type SubmissionStatus = "idle" | "success" | "warning" | "error";

interface SubmissionResult {
  status: SubmissionStatus;
  emailSent: boolean;
  enquiryCreated: boolean;
  emailError?: string;
}

const studyNeedOptions = [
  "1-on-1 Tutoring",
  "Assignment Help",
  "Dissertation Help",
  "Coding Project Help",
  "Research Paper Help",
  "Presentation Help",
  "Exam Subject Help",
];

const studyNeedLabels: Record<string, string> = {
  "1-on-1 Tutoring": "1-on-1 Tutoring",
  "Assignment Help": "Assignment Help",
  "Dissertation Help": "Dissertation Help",
  "Coding Project Help": "Coding Project Help",
  "Research Paper Help": "Research Paper Help",
  "Presentation Help": "Presentation Help",
  "Exam Subject Help": "Exam Subject Help",
};

function GetHelpForm({
  formSize = "lg",
  flatForm = false,
  headerMode = "default",
  submitLabel = "Get Matched With an Expert",
  showEmailField = false,
}: GetHelpFormProps) {
  const theme = useMantineTheme();
  const [formData, setFormData] = useState<LeadFormData>({
    usersName: "",
    email: "",
    contactNumber: "",
    studyNeed: "",
    projectRequirements: "",
    allowMarketingNotifications: true,
  });
  const [errors, setErrors] = useState<LeadFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [
    shouldShowAlertModal,
    { open: showAlertModal, close: closeAlertModal },
  ] = useDisclosure(false);
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult>({
    status: "idle",
    emailSent: false,
    enquiryCreated: false,
  });

  const inputSize = formSize === "lg" ? "md" : formSize;
  const pairedFieldCols = formSize === "sm"
    ? { base: 1, md: 2 }
    : { base: 1, sm: 2 };
  const whatsappUrl = buildWhatsAppUrl(
    `Hi, I'm looking for a tutor to help me with ${studyNeedLabels[formData.studyNeed] || "my studies"}.`,
  );

  const setFieldValue = <K extends keyof LeadFormData>(
    field: K,
    value: LeadFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const nextErrors: LeadFormErrors = {};

    if (!formData.usersName.trim()) {
      nextErrors.usersName = "Enter your name.";
    }

    if (showEmailField && formData.email.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailPattern.test(formData.email.trim())) {
        nextErrors.email = "Enter a valid email address.";
      }
    }

    if (!formData.contactNumber.trim()) {
      nextErrors.contactNumber = "Enter your phone number.";
    } else if (!/^\+?[0-9\s()-]{7,15}$/.test(formData.contactNumber.trim())) {
      nextErrors.contactNumber = "Enter a valid phone number.";
    }

    if (!formData.studyNeed) {
      nextErrors.studyNeed = "Select the kind of support you need.";
    }

    if (!formData.projectRequirements.trim()) {
      nextErrors.projectRequirements =
        "Describe what you'd like to learn so we can match you with the right tutor.";
    } else if (formData.projectRequirements.trim().length < 20) {
      nextErrors.projectRequirements =
        "Add a bit more detail so we can understand your learning goals.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildLeadSummary = () => {
    const lines = [
      `Support needed: ${formData.studyNeed}`,
      "Lead details:",
      formData.projectRequirements.trim(),
    ];

    return lines.join("\n");
  };

  const getErrorMessage = (error: unknown) => {
    if (typeof error === "string") {
      return error;
    }

    if (error && typeof error === "object") {
      const errorWithText = error as {
        text?: string;
        message?: string;
        status?: number;
      };

      return (
        errorWithText.text ||
        errorWithText.message ||
        (errorWithText.status ? `Request failed with status ${errorWithText.status}` : "")
      );
    }

    return "";
  };

  const handleSubmit = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    if (isLoading || !validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const [emailResult, enquiryResult] = await Promise.allSettled([
        emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID as string,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID as string,
          {
            project_requirements: buildLeadSummary(),
            budget: "",
            users_name: formData.usersName.trim(),
            contact_number: formData.contactNumber.trim(),
            deadline: "Flexible",
            email: formData.email.trim(),
            study_need: formData.studyNeed,
          },
        ),
        createEnquiry(
          formData.studyNeed,
          formData.usersName.trim(),
          "+1",
          formData.contactNumber.trim(),
          formData.email.trim(),
          formData.allowMarketingNotifications,
          "",
        ),
      ]);

      const emailSent = emailResult.status === "fulfilled";
      const enquiryCreated = enquiryResult.status === "fulfilled";
      const emailError =
        emailResult.status === "rejected"
          ? getErrorMessage(emailResult.reason)
          : "";

      if (!emailSent) {
        console.log("EmailJS send failed:", emailResult.reason);
      }

      if (!enquiryCreated) {
        console.log(
          "Enquiry API save failed:",
          enquiryResult.status === "rejected" ? enquiryResult.reason : "",
        );
      }

      if (emailSent || enquiryCreated) {
        trackGoogleAdsLeadFormConversion({
          form_path: window.location.pathname,
          lead_type: formData.studyNeed,
        });
      }

      if (emailSent && enquiryCreated) {
        setSubmissionResult({
          status: "success",
          emailSent,
          enquiryCreated,
        });
        resetForm();
      } else if (emailSent || enquiryCreated) {
        setSubmissionResult({
          status: "warning",
          emailSent,
          enquiryCreated,
          emailError,
        });
        resetForm();
      } else {
        setSubmissionResult({
          status: "error",
          emailSent,
          enquiryCreated,
          emailError,
        });
      }
    } catch (error) {
      console.log("Unexpected lead form failure:", error);
      setSubmissionResult({
        status: "error",
        emailSent: false,
        enquiryCreated: false,
        emailError: getErrorMessage(error),
      });
    } finally {
      setIsLoading(false);
      showAlertModal();
    }
  };

  const resetForm = () => {
    setFormData({
      usersName: "",
      email: "",
      contactNumber: "",
      studyNeed: "",
      projectRequirements: "",
      allowMarketingNotifications: true,
    });
    setErrors({});
  };

  return (
    <>
      <Paper
        p="0"
        radius="md"
        shadow={flatForm ? "none" : "sm"}
        withBorder={flatForm && headerMode === "default"}
        style={{
          overflow: "hidden",
          background: theme.white,
        }}
      >
        {headerMode === "default" ? (
          <Box
            px="md"
            py="sm"
            style={{
              background:
                "linear-gradient(135deg,var(--mantine-color-green-6, #38d9a9) 0%,var(--mantine-color-teal-5, #20c997) 100%)",
              color: theme.white,
            }}
          >
            <Stack gap={4}>
              <GroupHeader />
              <Text size="sm" maw={520}>
                Share what you are studying and where you need support. We'll
                match you with a tutor who can guide you toward stronger
                understanding and skills.
              </Text>
            </Stack>
          </Box>
        ) : null}

        <Box component="form" onSubmit={handleSubmit} px="md" py="sm">
          <Stack gap={10}>
            {headerMode === "default" ? (
              <Alert
                color="green"
                variant="light"
                radius="md"
                title="Fastest way to hear back"
              >
                Short, specific requests convert better. Add your subject and the
                exact help you need.
              </Alert>
            ) : null}

            <TextInput
              label="Full name"
              placeholder="Your name"
              value={formData.usersName}
              onChange={(event) =>
                setFieldValue("usersName", event.currentTarget.value)
              }
              error={errors.usersName}
              name="name"
              autoComplete="name"
              size={inputSize}
              radius="md"
              required
            />

            {showEmailField ? (
              <TextInput
                label="Email"
                placeholder="name@example.com"
                value={formData.email}
                onChange={(event) =>
                  setFieldValue("email", event.currentTarget.value)
                }
                error={errors.email}
                name="email"
                autoComplete="email"
                type="email"
                size={inputSize}
                radius="md"
              />
            ) : null}

            <SimpleGrid cols={pairedFieldCols} spacing="md">
              <TextInput
                label="Phone / WhatsApp"
                placeholder="Enter your number"
                value={formData.contactNumber}
                onChange={(event) =>
                  setFieldValue("contactNumber", event.currentTarget.value)
                }
                error={errors.contactNumber}
                name="tel"
                autoComplete="tel"
                inputMode="tel"
                type="tel"
                size={inputSize}
                radius="md"
                required
              />

              <Select
                label="What do you need?"
                placeholder="Choose one"
                data={studyNeedOptions}
                value={formData.studyNeed}
                onChange={(value) => setFieldValue("studyNeed", value || "")}
                error={errors.studyNeed}
                size={inputSize}
                radius="md"
                searchable={false}
                allowDeselect={false}
                required
              />
            </SimpleGrid>

            <Textarea
              label="What should we know?"
              placeholder="Subject, level, topic, deliverable, and where you are stuck."
              value={formData.projectRequirements}
              onChange={(event) =>
                setFieldValue("projectRequirements", event.currentTarget.value)
              }
              error={errors.projectRequirements}
              minRows={3}
              maxRows={4}
              autosize
              size={inputSize}
              radius="md"
              required
            />

            <Checkbox
              checked={formData.allowMarketingNotifications}
              onChange={(event) =>
                setFieldValue(
                  "allowMarketingNotifications",
                  event.currentTarget.checked,
                )
              }
              label="You can contact me by email or phone about this request."
              size="sm"
            />

            <Button
              type="submit"
              size={formSize}
              radius="md"
              loading={isLoading}
              leftSection={<IconBolt size={18} />}
            >
              {submitLabel}
            </Button>

            <Text size="xs" c="dimmed">
              We use your details only to review your request and respond on{" "}
              {showEmailField ? "WhatsApp or email." : "phone or WhatsApp."}
            </Text>

            <Button
              variant="subtle"
              color="green"
              size="sm"
              radius="md"
              component="a"
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
            >
              Prefer WhatsApp instead?
            </Button>
          </Stack>
        </Box>
      </Paper>

      <AlertModal
        opened={shouldShowAlertModal}
        closeModal={closeAlertModal}
        hasError={submissionResult.status === "error"}
      >
        <Text component="p">
          {submissionResult.status === "success" ? (
            <>
              Thanks. Your lead has been submitted and the team will review it
              shortly.
              <br />
              <br />
              Typical response time is within 2 to 4 hours during business
              hours.
            </>
          ) : submissionResult.status === "warning" ? (
            <>
              Your request was submitted, but one delivery channel is
              temporarily unavailable.
              <br />
              <br />
              {submissionResult.enquiryCreated ? (
                <>We saved your enquiry successfully and the team can still review it.</>
              ) : submissionResult.emailSent ? (
                <>We sent your request notification, but could not sync it to the enquiry system.</>
              ) : null}
              <br />
              <br />
              If you need a faster response, continue on WhatsApp as well.
            </>
          ) : (
            <>
              We could not submit the form right now. Please try again in a few
              minutes or continue on WhatsApp.
              {submissionResult.emailError?.toLowerCase().includes("invalid grant")
                ? " The connected Gmail account in EmailJS likely needs to be reconnected."
                : ""}
            </>
          )}
        </Text>
      </AlertModal>
    </>
  );
}

function GroupHeader() {
  return (
    <Box>
      <Stack gap={4}>
        <ThemeIcon size={34} radius="xl" variant="white" color="green">
          <IconLock size={18} />
        </ThemeIcon>
        <Title order={4}>Get Expert Help</Title>
      </Stack>
    </Box>
  );
}

export default GetHelpForm;
